import * as React from "react"
import { Control, Emitter } from 'rete'
import { EventsTypes } from "rete/types/events"

interface CheckboxSelectControlProps {
    emitter: Emitter<EventsTypes> | null
    getId: () => string[] | undefined
    setId: (x: string[]) => {}
    getOptions: () => Array<SelectControlOptions>
    change: () => void
    label: string | undefined
    minWidth?: string
}

export interface SelectControlOptions {
    id: number
    name: string
    gridtype? : string
}

type EmitterWithOff = Emitter<EventsTypes> & {
    off: (event: string, handler: (...args: unknown[]) => void) => void
}

const hasOff = (emitter: Emitter<EventsTypes> | null): emitter is EmitterWithOff =>
    !!emitter && typeof (emitter as Partial<EmitterWithOff>).off === "function"

const CheckboxSelectInput = ({ emitter, getId, setId, getOptions, change, label, minWidth }: CheckboxSelectControlProps) => {

    // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
    const [, forceUpdate] = React.useReducer(x => x + 1, 0)
    const [isOpen, setIsOpen] = React.useState(false)
    const dropdownRef = React.useRef<HTMLDivElement>(null)

    // Listen for process events to force re-render when options might have changed
    React.useEffect(() => {
        if (!emitter) return
        
        const handler = () => {
            // Small delay to ensure node data is updated
            setTimeout(() => forceUpdate(), 0)
        }
        
        emitter.on("process", handler)
        return () => {
            if (hasOff(emitter)) {
                emitter.off("process", handler)
            }
        }
    }, [emitter])

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const stopPropagation = (event: React.SyntheticEvent) => {
        event.stopPropagation()
    }

    const stopWheel = (event: React.WheelEvent) => {
        event.stopPropagation()
        event.preventDefault()
    }

    const toggleDropdown = (event: React.SyntheticEvent) => {
        event.stopPropagation()
        setIsOpen(!isOpen)
    }

    const handleCheckboxChange = (optionId: string, checked: boolean) => {
        const currentIds = getId() || []
        let newIds: string[]
        
        if (checked) {
            newIds = [...currentIds, optionId]
        } else {
            newIds = currentIds.filter(id => id !== optionId)
        }
        
        setId(newIds)
        change()
        forceUpdate()
        emitter?.trigger("process")
    }

    const getSelectedIds = (): string[] => {
        const ids = getId()
        return ids ?? []
    }

    const getSelectedNames = (): string => {
        if (selectedIds.length === 0) {
            return "None selected"
        }
        if (allSelected) {
            return "All selected"
        }
        if (selectedIds.length === 1) {
            const option = options.find(opt => String(opt.id) === selectedIds[0])
            return option?.name || "1 selected"
        }
        return `${selectedIds.length} selected`
    }

    const renderOptions = (options: SelectControlOptions[], selectedIds: string[]) =>
        [...options].map(opt => {
            const optionId = String(opt.id)
            const isChecked = selectedIds.includes(optionId)
            
            return (
                <label
                    key={opt.id}
                    style={{
                        display: "block",
                        padding: "4px 8px",
                        cursor: "pointer",
                        userSelect: "none",
                        color: "#000",
                        borderLeft: isChecked ? "2px solid #28a745" : "2px solid transparent",
                        backgroundColor: isChecked ? "rgba(40, 167, 69, 0.08)" : "transparent",
                        fontSize: "0.9rem",
                        lineHeight: 1.3
                    }}
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                >
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleCheckboxChange(optionId, e.target.checked)}
                        onMouseDown={stopPropagation}
                        onPointerDown={stopPropagation}
                        style={{ 
                            marginRight: "8px",
                            border: isChecked ? "2px solid #28a745" : "1px solid #ced4da",
                            borderRadius: "3px"
                        }}
                    />
                    {opt.name}
                </label>
            )
        })

    const options = getOptions()
    const selectedIds = getSelectedIds()

    const allOptionIds = options.map(opt => String(opt.id))
    const allSelected = allOptionIds.length > 0 && allOptionIds.every(id => selectedIds.includes(id))

    const toggleSelectAll = (checked: boolean) => {
        const newIds = checked ? allOptionIds : []
        setId(newIds)
        change()
        forceUpdate()
        emitter?.trigger("process")
    }
    const hasGridtype = options.length > 0 && options[0].gridtype

    if (hasGridtype) {
        const numeric = options.filter(opt => opt.gridtype == "NumericTileGrid")
        const categorical = options.filter(opt => opt.gridtype == "CategoricalTileGrid")
        const boolean = options.filter(opt => opt.gridtype == "BooleanTileGrid")

        return (
            <div style={{ display: "block", position: "relative" }} ref={dropdownRef}>
                {label && <label style={{ display: "block", marginBottom: "2px", color: "white", fontSize: "0.85rem", fontWeight: 600 }}>{label}</label>}
                <button
                    type="button"
                    className="d-block"
                    style={{
                        minWidth: minWidth,
                        width: "100%",
                        textAlign: "left",
                        padding: "4px 10px",
                        border: "1px solid #ced4da",
                        borderRadius: "3px",
                        backgroundColor: "white",
                        color: "#000",
                        cursor: "pointer",
                        position: "relative",
                        appearance: "none",
                        WebkitAppearance: "none",
                        fontSize: "0.9rem",
                        lineHeight: 1.3
                    }}
                    onClick={toggleDropdown}
                    onPointerDown={stopPropagation}
                    onMouseDown={stopPropagation}
                    onTouchStart={stopPropagation}
                    onWheel={stopWheel}
                >
                    {getSelectedNames()}
                    <span style={{ float: "right", marginLeft: "8px" }}>
                        {isOpen ? "▲" : "▼"}
                    </span>
                </button>
                {isOpen && (
                    <div
                        style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            minWidth: minWidth,
                            backgroundColor: "white",
                            border: "1px solid #ced4da",
                            borderRadius: "3px",
                            marginTop: "2px",
                            maxHeight: "260px",
                            overflowY: "auto",
                            zIndex: 1000,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                        }}
                        onMouseDown={stopPropagation}
                        onPointerDown={stopPropagation}
                        onWheel={stopWheel}
                    >
                        <label
                    style={{
                        display: "block",
                        padding: "4px 8px",
                        cursor: "pointer",
                        userSelect: "none",
                        color: "#000",
                        borderLeft: allSelected ? "2px solid #28a745" : (selectedIds.length > 0 ? "2px solid #28a745" : "2px solid transparent"),
                        backgroundColor: allSelected ? "rgba(40, 167, 69, 0.08)" : (selectedIds.length > 0 ? "rgba(40, 167, 69, 0.04)" : "transparent"),
                        borderBottom: "1px solid #eee",
                        fontSize: "0.9rem",
                        lineHeight: 1.3
                    }}
                            onMouseDown={stopPropagation}
                            onPointerDown={stopPropagation}
                        >
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && !allSelected }}
                                onChange={(e) => toggleSelectAll(e.target.checked)}
                                onMouseDown={stopPropagation}
                                onPointerDown={stopPropagation}
                                style={{
                                    marginRight: "8px",
                                    border: allSelected ? "2px solid #28a745" : "1px solid #ced4da",
                                    borderRadius: "3px"
                                }}
                            />
                            Select all
                        </label>
                        {numeric.length > 0 && (
                            <>
                                <div style={{ padding: "4px 8px", fontWeight: "bold", backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6", color: "#000" }}>
                                    Numeric
                                </div>
                                {renderOptions(numeric, selectedIds)}
                            </>
                        )}
                        {categorical.length > 0 && (
                            <>
                                <div style={{ padding: "4px 8px", fontWeight: "bold", backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6", borderTop: "1px solid #dee2e6", color: "#000" }}>
                                    Categorical
                                </div>
                                {renderOptions(categorical, selectedIds)}
                            </>
                        )}
                        {boolean.length > 0 && (
                            <>
                                <div style={{ padding: "4px 8px", fontWeight: "bold", backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6", borderTop: "1px solid #dee2e6", color: "#000" }}>
                                    Boolean
                                </div>
                                {renderOptions(boolean, selectedIds)}
                            </>
                        )}
                    </div>
                )}
            </div>
        )
    } else {
        return (
            <div style={{ display: "block", position: "relative" }} ref={dropdownRef}>
                {label && <label style={{ display: "block", marginBottom: "2px", color: "white", fontSize: "0.85rem", fontWeight: 600 }}>{label}</label>}
                <button
                    type="button"
                    className="d-block"
                    style={{
                        minWidth: minWidth,
                        width: "100%",
                        textAlign: "left",
                        padding: "4px 10px",
                        border: "1px solid #ced4da",
                        borderRadius: "3px",
                        backgroundColor: "white",
                        color: "#000",
                        cursor: "pointer",
                        position: "relative",
                        appearance: "none",
                        WebkitAppearance: "none",
                        fontSize: "0.9rem",
                        lineHeight: 1.3
                    }}
                    onClick={toggleDropdown}
                    onPointerDown={stopPropagation}
                    onMouseDown={stopPropagation}
                    onTouchStart={stopPropagation}
                    onWheel={stopWheel}
                >
                    {getSelectedNames()}
                    <span style={{ float: "right", marginLeft: "8px" }}>
                        {isOpen ? "▲" : "▼"}
                    </span>
                </button>
                {isOpen && (
                    <div
                        style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            minWidth: minWidth,
                            backgroundColor: "white",
                            border: "1px solid #ced4da",
                            borderRadius: "3px",
                            marginTop: "2px",
                            maxHeight: "260px",
                            overflowY: "auto",
                            zIndex: 1000,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                        }}
                        onMouseDown={stopPropagation}
                        onPointerDown={stopPropagation}
                        onWheel={stopWheel}
                    >
                        <label
                        style={{
                            display: "block",
                            padding: "4px 8px",
                            cursor: "pointer",
                            userSelect: "none",
                            color: "#000",
                            borderLeft: allSelected ? "2px solid #28a745" : (selectedIds.length > 0 ? "2px solid #28a745" : "2px solid transparent"),
                            backgroundColor: allSelected ? "rgba(40, 167, 69, 0.08)" : (selectedIds.length > 0 ? "rgba(40, 167, 69, 0.04)" : "transparent"),
                            borderBottom: "1px solid #eee",
                            fontSize: "0.9rem",
                            lineHeight: 1.3
                        }}
                            onMouseDown={stopPropagation}
                            onPointerDown={stopPropagation}
                        >
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && !allSelected }}
                                onChange={(e) => toggleSelectAll(e.target.checked)}
                                onMouseDown={stopPropagation}
                                onPointerDown={stopPropagation}
                                style={{
                                    marginRight: "8px",
                                    border: allSelected ? "2px solid #28a745" : "1px solid #ced4da",
                                    borderRadius: "3px"
                                }}
                            />
                            Select all
                        </label>
                        {renderOptions(options, selectedIds)}
                    </div>
                )}
            </div>
        )
    }
}


export class CheckboxSelectControl extends Control {
    props: CheckboxSelectControlProps
    component: (props: CheckboxSelectControlProps) => JSX.Element
    type: string

    constructor(emitter: Emitter<EventsTypes> | null, key: string, getOptions: () => Array<SelectControlOptions>, change: () => void, label: string | undefined = undefined, minWidth: string | undefined = undefined) {
        super(key)
        this.type = 'CheckboxSelectControl'

        this.props = {
            emitter,
            getId: () => {
                const data = this.getData(key)
                // Ensure we return an array
                if (Array.isArray(data)) {
                    return data.map(String)
                }
                if (data !== undefined && data !== null) {
                    return [String(data)]
                }
                return undefined
            },
            setId: (ids: string[]) => {
                this.putData(key, ids)
                return {}
            },
            getOptions,
            change,
            label,
            minWidth
        }

        this.component = CheckboxSelectInput
    }
}

